/**
 * /pupillary-distance — контроллер прибора.
 *
 * Три входа в один результат:
 *   · КАМЕРА — живой замер: кадры идут через FaceLandmarker (общий примитив
 *     FRLandmarks, лестница источник × делегат), годные кадры (лицо анфас,
 *     взгляд в объектив, глаза открыты, 30–75 см) копятся, и когда их
 *     достаточно, замер заканчивается сам — человеку, который смотрит в
 *     объектив, нечем нажимать кнопку «готово»;
 *   · ФОТО — то же на снимке (сюда переехал метод /pd-meter);
 *   · ЗНАЮ PD — число из рецепта или от оптика переводится во всё остальное.
 *
 * Математика — в pd-core.js, поиск карты — в pd-card.js (оба проверяются в
 * Node: tests/js/pupillary-distance-core.test.mjs). Здесь только поведение.
 */
(function () {
    'use strict';

    var CFG = window.__PD || {};
    var T = CFG.i18n || {};
    var SLUG = 'pupillary-distance';
    var $ = function (id) { return document.getElementById(id); };
    var root = $('pd');
    if (!root) return;

    // Скрипты зависимостей грузятся с defer и могут прийти запасным путём
    // позже этого файла — ждём их, а не падаем на undefined.
    function whenReady(names, cb, waited) {
        waited = waited || 0;
        var missing = names.filter(function (n) { return !window[n]; });
        if (!missing.length) return cb();
        if (waited > 15000) { cb(missing); return; }
        setTimeout(function () { whenReady(names, cb, waited + 100); }, 100);
    }

    var LOC = String(window.__LOCALE || document.documentElement.lang || 'en').toLowerCase();
    function t(key, rep) {
        var s = T[key];
        if (s == null) s = key;
        if (rep) {
            Object.keys(rep).sort(function (a, b) { return b.length - a.length; }).forEach(function (k) {
                s = String(s).split(':' + k).join(rep[k]);
            });
        }
        return s;
    }
    var nf;
    try { nf = new Intl.NumberFormat(LOC === 'ar' ? 'ar-u-nu-latn' : LOC, { maximumFractionDigits: 1 }); }
    catch (_) { nf = { format: function (v) { return String(v); } }; }
    function f1(v) { return nf.format(Math.round(v * 10) / 10); }
    function fh(v) { return nf.format(window.PDCore.roundHalf(v)); }
    function bin(b) { return b ? nf.format(b[0]) + '–' + nf.format(b[1]) : '—'; }

    function ev(f, data) { try { if (window.__ae) window.__ae('feature_use', Object.assign({ f: f }, data || {})); } catch (_) { } }
    function stageMark(n, m) { try { if (window.__stage) window.__stage(n, m || {}); } catch (_) { } }
    function diag(issue, data) { try { if (window.__aeDiagnostic) window.__aeDiagnostic(Object.assign({ issue: issue }, data || {})); } catch (_) { } }
    function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (_) { } }
    function announce(msg) { try { if (window.__announce) window.__announce(msg); } catch (_) { } }
    // Атрибутом, а не свойством: у SVG-элементов свойства `hidden` нет, и
    // присваивание `svg.hidden = true` молча ничего не делает.
    function show(id, on) {
        var el = typeof id === 'string' ? $(id) : id;
        if (!el) return;
        if (on) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    }
    function tick() { return new Promise(function (r) { setTimeout(r, 0); }); }
    function frameTick() { return new Promise(function (r) { requestAnimationFrame(function () { r(); }); }); }
    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
    function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }

    var COARSE = false;
    try { COARSE = window.matchMedia('(pointer:coarse)').matches; } catch (_) { }

    var S = {
        mode: 'camera', scale: 'card', state: 'idle',
        runs: [], cur: null, known: null,
        reading: 400, voice: false,
        hfov: 68,
        cam: null, stream: null, loopId: 0, wake: null,
        live: null, view: null, zoom: true, drag: null, sel: 'r',
        calib: null,
    };

    /* ═══ состояние и режимы ═══════════════════════════════════════════════ */

    var stagePanel = $('pdStagePanel');

    function setState(st) {
        S.state = st;
        root.dataset.state = st;
        var known = S.mode === 'known';
        var hasFrame = !!(S.cur && S.cur.frame);
        var live = st === 'live';
        var result = st === 'result';
        show('pdIdle', st === 'idle' || st === 'acquire' || (result && known));
        show('pdIdleCta', !known && (st === 'idle' || st === 'acquire'));
        show('pdIdleNote', !known && (st === 'idle' || st === 'acquire'));
        show('pdArt', !known);
        show('pdGlasses', known);
        show('pdVideo', live);
        show('pdCanvas', live || (result && !known && hasFrame));
        show('pdHud', live);
        show('pdBusy', st === 'loading');
        var review = result && !known && hasFrame;
        show('pdReview', review);
        stagePanel.classList.toggle('has-review', review);
        show('pdPanelCamera', S.mode === 'camera' && (st === 'idle' || st === 'acquire'));
        show('pdPanelPhoto', S.mode === 'photo');
        show('pdPhotoTips', S.mode === 'photo' && !result);
        show('pdPanelKnown', known);
        show('pdChecks', S.mode === 'camera' && live);
        show('pdResult', result);
        show('pdDrawers', result);
        var cv = $('pdCanvas');
        cv.classList.toggle('can-drag', review);
        if (!live) hideLoupe();
        if (st !== 'acquire' && st !== 'live' && !(st === 'loading' && S.mode === 'camera')) unfitStage();
        requestAnimationFrame(redraw);
    }

    /* Высота сцены на живой замер — по месту, что осталось на экране ниже её
       верха. Страницу за человека не прокручиваем (правило сайта), поэтому
       подстраивается сама сцена: камера и подсказка должны быть видны целиком
       там, где человек нажал «Включить камеру». */
    function topInset() {
        var y = 0;
        try {
            var els = document.querySelectorAll('header, .site-header, .topbar, [class*=banner]');
            for (var i = 0; i < els.length; i++) {
                var cs = getComputedStyle(els[i]);
                if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
                var r = els[i].getBoundingClientRect();
                if (r.top <= 1 && r.bottom > y && r.bottom < 220) y = r.bottom;
            }
        } catch (_) { }
        return y;
    }
    function fitStage() {
        var st = $('pdStage');
        var r = st.getBoundingClientRect();
        var vh = window.innerHeight || 800;
        var top = Math.max(r.top, topInset());
        if (top > vh * 0.62) { unfitStage(); return; }
        var narrow = root.getBoundingClientRect().width < 760;
        // на узком — под сценой ещё строка проверок; на широком она справа
        var reserve = narrow ? 150 : 14;
        var h = clamp(vh - top - reserve, narrow ? 280 : 340, narrow ? 560 : 640);
        st.style.setProperty('--pd-fit-h', Math.round(h) + 'px');
        st.classList.add('pd-fit');
    }
    function unfitStage() { $('pdStage').classList.remove('pd-fit'); }

    function setMode(m, opts) {
        if (S.mode === m && !(opts && opts.force)) return;
        if (S.state === 'live' || S.state === 'loading' || S.state === 'acquire') stopLive('mode');
        S.mode = m;
        root.dataset.mode = m;
        ['camera', 'photo', 'known'].forEach(function (k) {
            var b = $('pdTab' + k.charAt(0).toUpperCase() + k.slice(1));
            var on = k === m;
            b.classList.toggle('on', on);
            b.setAttribute('aria-selected', on ? 'true' : 'false');
            b.tabIndex = on ? 0 : -1;
        });
        S.cur = null;
        hideErr();
        $('pdStartLabel').textContent = m === 'photo' ? t('cta_photo') : t('cta_camera');
        $('pdIdleNote').textContent = m === 'photo' ? t('idle_note_photo') : t('idle_note');
        if (m === 'known') {
            setState(S.known ? 'result' : 'idle');
            if (S.known) renderResult();
            renderGlasses(S.known);
            setTimeout(function () { try { $('pdKnownIn').focus({ preventScroll: true }); } catch (_) { } }, 30);
        } else {
            setState('idle');
        }
        ev('pd_mode', { m: m });
    }

    function setScale(sc) {
        S.scale = sc;
        root.dataset.scale = sc;
        Array.prototype.forEach.call(document.querySelectorAll('.pd-sw'), function (b) {
            var on = b.getAttribute('data-scale') === sc;
            b.classList.toggle('on', on);
            b.setAttribute('aria-checked', on ? 'true' : 'false');
            b.tabIndex = on ? 0 : -1;
        });
        try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'scale', sc); } catch (_) { }
        syncScaleSub();
    }
    function syncScaleSub() {
        $('pdScaleSub').textContent = S.scale === 'card' ? t('scale_card_sub') : (S.calib ? t('scale_iris_sub_cal') : t('scale_iris_sub'));
    }

    /* ═══ ошибки на сцене ══════════════════════════════════════════════════ */

    function showErr(msg) { var e = $('pdIdleErr'); e.textContent = msg; e.hidden = false; }
    function hideErr() { $('pdIdleErr').hidden = true; }
    function busy(text, frac) {
        $('pdBusyText').textContent = text || '';
        var wrap = $('pdBusyBarWrap');
        if (frac == null) { wrap.hidden = true; }
        else { wrap.hidden = false; $('pdBusyBar').style.width = Math.round(clamp(frac, 0.04, 1) * 100) + '%'; }
    }

    /* ═══ детектор лица ════════════════════════════════════════════════════ */

    var unwatch = null;
    function ensureDetector(mode) {
        var FR = window.FRLandmarks;
        if (!FR) return Promise.reject(new Error('landmarks-missing'));
        if (!unwatch && FR.onProgress) {
            unwatch = FR.onProgress(function (phase, got, total) {
                if (S.state !== 'loading') return;
                if (phase === 'model') busy(t('loading_model'), total > 0 ? got / total : null);
                else if (phase === 'init') busy(t('loading_init'));
            });
        }
        return FR.ensure(CFG.sources || [], {
            mode: mode, numFaces: 1, matrix: true, downloaderUrl: CFG.mdUrl,
        });
    }

    function detectorFailed(e) {
        var unavailable = window.FRLandmarks && window.FRLandmarks.isUnavailable && window.FRLandmarks.isUnavailable(e);
        diag('pd_detector_failed', { msg: String((e && e.message) || e).slice(0, 120), unavailable: unavailable ? 1 : 0 });
        showErr(unavailable ? t('err_detector') : t('err_model'));
    }

    /* ═══ камера ═══════════════════════════════════════════════════════════ */

    function hfovFor(W, H) {
        // Телефон держат вертикально: длинная сторона кадра — вертикаль, и угол
        // по ней у фронтальной камеры шире, чем у веб-камеры ноутбука.
        return (COARSE || H > W) ? window.PDCore.C.HFOV_PHONE_DEG : window.PDCore.C.HFOV_DESK_DEG;
    }

    async function startCamera() {
        if (S.state === 'live' || S.state === 'loading' || S.state === 'acquire') return;
        if (!window.CameraManager || !window.PDCore) { showErr(t('err_model')); return; }
        hideErr();
        S.cur = null;
        fitStage();
        setState('acquire');
        stageMark('camera_start', { scale: S.scale });
        ev('pd_camera_start', { scale: S.scale, runs: S.runs.length });
        var cm = S.cam || (S.cam = new window.CameraManager({ slug: SLUG }));
        var detP = ensureDetector('VIDEO');
        detP.catch(function () { });
        var stream;
        try {
            stream = await cm.start({ facingMode: 'user', width: 1920, height: 1080, button: $('pdStart') });
        } catch (err) {
            // CameraManager уже объяснил причину у кнопки (занята, запрещена,
            // не найдена) и предложил повтор — вторую фразу не пишем.
            ev('pd_camera_fail', { code: (err && err.code) || 'generic' });
            setState('idle');
            return;
        }
        S.stream = stream;
        setState('loading');
        busy(window.FRLandmarks && window.FRLandmarks.loaded() ? t('loading_camera') : t('loading_model'));
        try { await detP; }
        catch (e) { stopStream(); setState('idle'); detectorFailed(e); return; }
        var v = $('pdVideo');
        v.srcObject = stream;
        v.muted = true;
        v.playsInline = true;
        try { await v.play(); } catch (_) { }
        var waited = 0;
        while ((!v.videoWidth || v.readyState < 2) && waited < 6000) { await new Promise(function (r) { setTimeout(r, 60); }); waited += 60; }
        if (!v.videoWidth) { stopStream(); setState('idle'); showErr(t('err_no_frames')); diag('pd_no_frames', {}); return; }
        lockZoom(stream);
        S.hfov = hfovFor(v.videoWidth, v.videoHeight);
        beginLive();
    }

    // Зум меняет фокусное расстояние, а по нему считается дистанция до лица.
    function lockZoom(stream) {
        try {
            var tr = stream.getVideoTracks()[0];
            var caps = tr.getCapabilities ? tr.getCapabilities() : null;
            var set = tr.getSettings ? tr.getSettings() : {};
            if (caps && caps.zoom && set.zoom && set.zoom > caps.zoom.min) {
                tr.applyConstraints({ advanced: [{ zoom: caps.zoom.min }] }).catch(function () { });
            }
        } catch (_) { }
    }

    function stopStream() {
        try { if (S.cam) S.cam.stop(); } catch (_) { }
        if (S.stream) { try { S.stream.getTracks().forEach(function (tr) { tr.stop(); }); } catch (_) { } }
        S.stream = null;
        var v = $('pdVideo');
        try { v.pause(); v.srcObject = null; } catch (_) { }
        releaseWake();
    }

    // Экран не гаснет, пока человек смотрит в объектив, — через общий
    // примитив (он проверяет, что замок держится, и берёт его заново после
    // заморозки вкладки; где API нет — запасной путь через видео).
    function acquireWake() {
        try { if (window.__wakeLock && window.__wakeLock.request) { window.__wakeLock.request(); S.wake = true; } } catch (_) { }
    }
    function releaseWake() {
        try { if (S.wake && window.__wakeLock && window.__wakeLock.release) window.__wakeLock.release(); } catch (_) { }
        S.wake = null;
    }

    /* ═══ живой замер ══════════════════════════════════════════════════════ */

    var NEED_IRIS = 40, NEED_CARD = 30, NEED_CARD_FRAMES = 4, MAX_FRAMES = 6;

    function beginLive() {
        S.live = {
            t0: performance.now(), samples: [], frames: [], lastCap: 0,
            cardSeenAt: 0, cardHits: 0, cardBox: null, lastCardCheck: 0,
            issue: null, issueSince: 0, lastSpoken: 0, spoken: null, busy: false, dets: 0, relax: 0,
            nudged: false, lastFace: 0, frameN: 0, lastT: -1,
        };
        setState('live');
        guide(t('g_no_face'), 'warn');
        $('pdProg').style.width = '0%';
        $('pdProg2').style.width = '0%';
        acquireWake();
        stageMark('measuring', { scale: S.scale });
        if (S.voice) speak(t(S.scale === 'card' ? 'g_start_card' : 'g_start'));
        schedule();
    }

    function schedule() {
        var v = $('pdVideo');
        if (!S.live) return;
        if (v.requestVideoFrameCallback) S.loopId = v.requestVideoFrameCallback(onFrame);
        else S.loopId = requestAnimationFrame(onFrame);
    }

    function stopLoop() {
        var v = $('pdVideo');
        try { if (v.cancelVideoFrameCallback && S.loopId) v.cancelVideoFrameCallback(S.loopId); } catch (_) { }
        try { cancelAnimationFrame(S.loopId); } catch (_) { }
        S.loopId = 0;
    }

    function stopLive(why) {
        var L = S.live;
        S.live = null;
        stopLoop();
        stopStream();
        if (L) ev('pd_live_stop', { why: why || 'user', frames: L.samples.length, s: Math.round((performance.now() - L.t0) / 1000) });
        if (why !== 'finish') setState('idle');
    }

    function onFrame() {
        var L = S.live;
        if (!L || S.state !== 'live') return;
        var v = $('pdVideo');
        var W = v.videoWidth, H = v.videoHeight;
        if (!W || !H || L.busy) { schedule(); return; }
        // Один и тот же кадр дважды не считаем (rAF на старых браузерах тикает чаще камеры).
        if (v.currentTime === L.lastT && !v.requestVideoFrameCallback) { schedule(); return; }
        L.lastT = v.currentTime;
        var now = performance.now();
        var faces;
        try {
            faces = window.FRLandmarks.detectSync(v, W, H, { timestamp: now });
        } catch (e) {
            L.busy = true;
            window.FRLandmarks.recover(e).then(function () { L.busy = false; schedule(); }, function (e2) {
                L.busy = false; stopLive('detector'); detectorFailed(e2);
            });
            return;
        }
        L.dets++;
        var face = faces && faces[0];
        var fm = face ? window.PDCore.frameMetrics(face.pts, W, H, face.m) : null;
        // «Мягкие» претензии (поворот, наклон, взгляд) у части лиц держатся
        // всегда: асимметрия лица, посадка глаз, оценка позы MediaPipe. Если
        // одна и та же претензия стоит несколько секунд почти без изменений —
        // это особенность лица, а не движение, и допуск расширяется ступенями.
        // Без этого такой человек не закончил бы замер никогда.
        var k = 1 + 0.45 * L.relax;
        var q = window.PDCore.frameQuality(fm, {
            hfov: S.hfov, nearMm: 280, farMm: 760, minIrisPx: 16,
            maxYaw: 10 * k, maxRoll: 7 * k, maxPitch: 18 * k, maxGaze: 0.2 * k, maxIrisAsym: 0.14 * k,
        });
        if (fm) L.lastFace = now;

        // Карта: проверяем на уменьшенном кадре несколько раз в секунду —
        // и чтобы подсказать «приложите карту», и чтобы считать только кадры с ней.
        if (S.scale === 'card' && fm && now - L.lastCardCheck > 320 && Math.abs(fm.yaw) < 12) {
            L.lastCardCheck = now;
            var c = liveCard(v, W, H, fm);
            if (c && c.ok) { L.cardSeenAt = now; L.cardHits++; L.cardBox = c; }
            else if (now - L.cardSeenAt > 900) L.cardBox = null;
        }
        var cardOk = S.scale !== 'card' || (now - L.cardSeenAt < 1000);

        var issue = !fm ? 'no_face' : (!q.ok ? q.issue : (!cardOk ? 'card' : null));
        var ratio = fm ? fm.ipdPx / fm.irisH : 0;
        if (!issue && L.samples.length >= 6) {
            var rec = L.samples.slice(-12).map(function (s) { return s.ratio; });
            var med = window.PDCore.median(rec);
            if (Math.abs(ratio - med) / med > 0.035) issue = 'hold';
        }
        if (!issue) {
            L.samples.push({
                ratio: ratio, ipdPx: fm.ipdPx, irisH: fm.irisH, shareR: fm.monoR / (fm.monoR + fm.monoL),
                D: q.distanceMm, yaw: fm.yaw, t: now,
            });
            maybeCapture(v, W, H, face, fm, now);
        }

        drawLive(fm, W, H, now);
        updateLiveUi(issue, q, fm, now);
        var soft = issue === 'turn' || issue === 'tilt' || issue === 'chin' || issue === 'gaze';
        if (soft && L.relax < 2 && now - L.issueSince > (L.relax ? 4000 : 5000)) {
            L.relax++; L.issueSince = now;
            ev('pd_relax', { lvl: L.relax, issue: issue, yaw: fm ? Math.round(fm.yaw) : null });
        }

        var need = S.scale === 'card' ? NEED_CARD : NEED_IRIS;
        var got = L.samples.length;
        var cardFrames = L.frames.filter(function (f) { return f.card; }).length;
        var frac = S.scale === 'card'
            ? Math.min(got / need, 1) * 0.7 + Math.min(cardFrames / NEED_CARD_FRAMES, 1) * 0.3
            : got / need;
        setProgress(frac);
        if (got >= need && (S.scale !== 'card' || cardFrames >= NEED_CARD_FRAMES)) { finishLive(); return; }

        // Долго не выходит — говорим, что именно мешает, а не молчим.
        var el = now - L.t0;
        if (el > 60000) { stopLive('timeout'); showErr(t('err_timeout')); diag('pd_live_timeout', { frames: got, cardFrames: cardFrames, issue: issue || '' }); return; }
        schedule();
    }

    var smallCv = null;
    function liveCard(v, W, H, fm) {
        if (!window.PDCard) return null;
        var pxPerMm = fm.irisH / window.PDCore.C.HVID_MM;
        // Карта в уменьшенном кадре — около 120 px: хватает, чтобы увидеть её,
        // и стоит миллисекунды. Точный поиск — потом, на полных кадрах.
        var sc = clamp(120 / (85.6 * pxPerMm), 0.2, 1);
        var w = Math.round(W * sc), h = Math.round(H * sc);
        if (!smallCv) smallCv = document.createElement('canvas');
        if (smallCv.width !== w || smallCv.height !== h) { smallCv.width = w; smallCv.height = h; }
        var cx = smallCv.getContext('2d', { willReadFrequently: true });
        if (!window.__drawCameraFrame(cx, v, 0, 0, w, h)) return null;   // no frame this tick
        var img;
        try { img = cx.getImageData(0, 0, w, h); } catch (_) { return null; }
        var g = window.PDCard.luminance(img);
        var fms = scaleFm(fm, sc);
        var r = window.PDCard.find(g, w, h, fms, pxPerMm * sc, { angles: [0, -4, 4], minContrast: 2.0 });
        if (r && r.corners) r.corners = r.corners.map(function (p) { return { x: p.x / sc, y: p.y / sc }; });
        return r;
    }
    function scaleFm(fm, sc) {
        return { u: fm.u, brow: { x: fm.brow.x * sc, y: fm.brow.y * sc }, irisH: fm.irisH * sc };
    }

    function frameScore(fm) {
        return Math.abs(fm.yaw) + Math.abs(fm.roll) * 0.6 + Math.abs(fm.gazeR - fm.gazeL) * 40;
    }

    function maybeCapture(v, W, H, face, fm, now) {
        var L = S.live;
        if (now - L.lastCap < 170) return;
        var withCard = S.scale === 'card' && (now - L.cardSeenAt < 400);
        if (S.scale === 'card' && !withCard) return;
        var score = frameScore(fm);
        if (L.frames.length >= MAX_FRAMES) {
            var worst = 0;
            for (var i = 1; i < L.frames.length; i++) if (L.frames[i].score > L.frames[worst].score) worst = i;
            if (L.frames[worst].score <= score) return;
            L.frames.splice(worst, 1);
        }
        L.lastCap = now;
        var c = document.createElement('canvas');
        c.width = W; c.height = H;
        if (!window.__drawCameraFrame(c.getContext('2d'), v, 0, 0, W, H)) return;   // no frame: the next one is 170 ms away
        L.frames.push({ canvas: c, W: W, H: H, pts: face.pts.map(function (p) { return { x: p.x, y: p.y, z: p.z }; }), m: face.m ? face.m.slice() : null, fm: fm, score: score, card: withCard });
    }

    function setProgress(frac) {
        var p = Math.round(clamp(frac, 0, 1) * 100) + '%';
        $('pdProg').style.width = p;
        $('pdProg2').style.width = p;
    }

    function guide(text, kind) {
        var g = $('pdGuide');
        if (g.textContent !== text) g.textContent = text;
        g.className = 'pd-guide' + (kind ? ' ' + kind : '');
    }

    var GUIDE = {
        no_face: 'g_no_face', blink: 'g_blink', closer: 'g_closer', farther: 'g_farther',
        turn: 'g_turn', tilt: 'g_tilt', chin: 'g_chin', gaze: 'g_gaze', card: 'g_card', hold: 'g_hold_still',
    };

    function updateLiveUi(issue, q, fm, now) {
        var L = S.live;
        if (issue !== L.issue) { L.issue = issue; L.issueSince = now; }
        var text = issue ? t(GUIDE[issue] || 'g_turn') : t('g_hold');
        guide(text, issue ? 'warn' : 'ok');
        // Голос — только когда подсказка устоялась, и не чаще раза в 2.5 с.
        if (S.voice && now - L.issueSince > 650 && L.spoken !== issue && now - L.lastSpoken > 2500) {
            L.spoken = issue; L.lastSpoken = now;
            speak(text);
        }
        var D = q && q.distanceMm;
        var chip = $('pdDist');
        if (D) {
            chip.hidden = false;
            chip.textContent = t('hud_distance', { cm: Math.round(D / 10) });
            chip.classList.toggle('warn', issue === 'closer' || issue === 'farther');
        } else chip.hidden = true;
        setChk('face', fm ? 'ok' : 'bad');
        setChk('gaze', !fm ? '' : issue === 'gaze' || issue === 'blink' ? 'bad' : 'ok');
        setChk('pose', !fm ? '' : issue === 'turn' || issue === 'tilt' || issue === 'chin' ? 'bad' : 'ok');
        setChk('dist', !fm ? '' : issue === 'closer' || issue === 'farther' ? 'bad' : 'ok');
        $('pdChkDist').textContent = D ? t('hud_distance', { cm: Math.round(D / 10) }) : '';
        if (S.scale === 'card') setChk('card', now - L.cardSeenAt < 1000 ? 'ok' : (fm ? 'bad' : ''));
    }
    function setChk(k, st) {
        var li = root.querySelector('[data-chk="' + k + '"]');
        if (!li) return;
        li.classList.toggle('ok', st === 'ok');
        li.classList.toggle('bad', st === 'bad');
    }

    /* ── голос, звук, вибрация ─────────────────────────────────────────── */

    var VOICE_LANG = { ru: 'ru-RU', en: 'en-US', uk: 'uk-UA', de: 'de-DE', es: 'es-ES', fr: 'fr-FR', pt: 'pt-BR', id: 'id-ID', ko: 'ko-KR', ar: 'ar-SA' };
    function speak(text) {
        try {
            if (!window.speechSynthesis || !window.SpeechSynthesisUtterance) return;
            window.speechSynthesis.cancel();
            var u = new SpeechSynthesisUtterance(text);
            u.lang = VOICE_LANG[LOC] || LOC;
            u.rate = 1.05;
            window.speechSynthesis.speak(u);
        } catch (_) { }
    }
    var actx = null;
    function chime() {
        try {
            var AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return;
            actx = actx || new AC();
            if (actx.state === 'suspended') actx.resume().catch(function () { });
            var t0 = actx.currentTime;
            [880, 1320].forEach(function (f, i) {
                var o = actx.createOscillator(), g = actx.createGain();
                o.type = 'sine'; o.frequency.value = f;
                g.gain.setValueAtTime(0.0001, t0 + i * 0.12);
                g.gain.exponentialRampToValueAtTime(0.18, t0 + i * 0.12 + 0.02);
                g.gain.exponentialRampToValueAtTime(0.0001, t0 + i * 0.12 + 0.22);
                o.connect(g); g.connect(actx.destination);
                o.start(t0 + i * 0.12); o.stop(t0 + i * 0.12 + 0.25);
            });
        } catch (_) { }
        try { if (navigator.vibrate) navigator.vibrate(35); } catch (_) { }
    }

    /* ═══ конец живого замера → результат ══════════════════════════════════ */

    async function finishLive() {
        var L = S.live;
        stopLive('finish');
        chime();
        if (S.voice) speak(t('g_done'));
        setState('loading');
        busy(S.scale === 'card' ? t('busy_card') : t('busy_calc'));
        await frameTick();
        var P = window.PDCore;
        var agg = aggregate(L.samples);
        var frames = L.frames.slice();
        if (!frames.length) { setState('idle'); showErr(t('err_timeout')); return; }
        frames.sort(function (a, b) { return a.score - b.score; });

        var cur = {
            src: 'camera', scale: S.scale, mirror: true, agg: agg, edited: false,
            t0: L.t0, frames: frames.length,
        };
        if (S.scale === 'card') {
            var hits = [];
            for (var i = 0; i < frames.length; i++) {
                var fr = frames[i];
                var r = findCard(fr);
                if (r && r.ok) {
                    var res = P.compute({ ipdPx: fr.fm.ipdPx, irisH: fr.fm.irisH, monoR: fr.fm.monoR, monoL: fr.fm.monoL,
                        W: fr.W, H: fr.H, D: agg.D, scale: 'card', cardPx: r.widthPx, yaw: fr.fm.yaw });
                    hits.push({ fr: fr, card: r, res: res });
                }
                await tick();
            }
            ev('pd_card_auto', { frames: frames.length, found: hits.length });
            if (hits.length) {
                var pds = hits.map(function (h) { return h.res.pd; });
                var medPd = P.median(pds);
                hits.sort(function (a, b) { return Math.abs(a.res.pd - medPd) - Math.abs(b.res.pd - medPd); });
                var best = hits[0];
                var noise = hits.length > 2 ? P.mad(pds, medPd) * 1.4826 : 0.6;
                cur.frame = best.fr;
                cur.card = { l: best.card.left, r: best.card.right, auto: true, contrast: best.card.contrast, aspect: best.card.aspect };
                // Кадры не вполне независимы (соседние, та же поза) — считаем их
                // вдвое меньше, чем есть; систематика кадрами не лечится вовсе.
                var nEff = Math.max(1, hits.length / 2);
                var r0 = hits[0].res;
                cur.multi = {
                    pd: medPd, shareR: P.median(hits.map(function (h) { return h.res.R / h.res.pd; })),
                    sd: Math.sqrt(r0.sysMm * r0.sysMm + r0.randMm * r0.randMm / nEff + noise * noise / hits.length),
                    n: hits.length,
                    hvid: P.median(hits.map(function (h) { return h.fr.fm.irisH / h.res.pxPerMm; })),
                };
            } else {
                cur.frame = frames[0];
                cur.card = expectedCard(frames[0].fm, agg.D);
                cur.card.auto = false;
            }
        } else {
            cur.frame = frames[0];
        }
        cur.marks = marksFrom(cur.frame.fm, cur.card);
        cur.orig = JSON.parse(JSON.stringify(cur.marks));
        S.cur = cur;
        S.zoom = true;
        syncZoomBtn();
        recalc();
        if (cur.scale === 'card' && cur.multi && cur.multi.n >= 2) saveCalib(cur.multi.hvid);
        setState('result');
        renderResult();
        pushRun();
        var r0 = cur.res;
        try {
            if (window.__aeToolComplete) window.__aeToolComplete({
                mode: 'camera', scale: cur.scale, pd: r0 ? Math.round(r0.pd * 10) / 10 : null, pm: r0 ? r0.pm : null,
                card_auto: cur.card ? (cur.card.auto ? 1 : 0) : null, cf: cur.multi ? cur.multi.n : 0, relax: L.relax,
                dcm: Math.round(agg.D / 10), s: Math.round((performance.now() - L.t0) / 1000),
            });
        } catch (_) { }
        announce(r0 ? t('announce_result', { pd: fh(r0.pd) }) : t('card_not_found'));
    }

    function aggregate(samples) {
        var P = window.PDCore;
        var ratios = samples.map(function (s) { return s.ratio; });
        var m = P.median(ratios);
        var irisH = P.median(samples.map(function (s) { return s.irisH; }));
        return {
            n: samples.length, ratio: m, irisH: irisH,
            shareR: P.median(samples.map(function (s) { return s.shareR; })),
            D: P.median(samples.map(function (s) { return s.D || 450; })),
            yaw: P.median(samples.map(function (s) { return s.yaw; })),
            // шум замера в мм: разброс отношения, пересчитанный в PD
            noiseRel: samples.length > 3 ? P.mad(ratios, m) * 1.4826 / m / Math.sqrt(samples.length) : 0.01,
        };
    }

    var fullCv = null;
    function frameGray(fr) {
        var cx = fr.canvas.getContext('2d', { willReadFrequently: true });
        var img = cx.getImageData(0, 0, fr.W, fr.H);
        return window.PDCard.luminance(img);
    }
    function findCard(fr) {
        if (!window.PDCard) return null;
        var P = window.PDCore;
        var D = P.distanceMm(fr.fm, S.hfov) || 450;
        try {
            return window.PDCard.find(frameGray(fr), fr.W, fr.H, fr.fm, fr.fm.irisH / P.C.HVID_MM,
                { depthFactor: P.cardDepthFactor(D) });
        } catch (e) { diag('pd_card_find_err', { msg: String(e && e.message).slice(0, 100) }); return null; }
    }

    // Где карта ДОЛЖНА лежать при масштабе радужки — стартовые метки, когда
    // автоматический поиск её не нашёл.
    function expectedCard(fm, D) {
        var P = window.PDCore;
        var px = fm.irisH / P.C.HVID_MM * P.cardDepthFactor(D || 450);
        var w = 85.6 * px, h = w * 53.98 / 85.6;
        var nx = fm.u.y, ny = -fm.u.x;
        var cy = h / 2 + 6 * px;
        var c = { x: fm.brow.x + nx * cy, y: fm.brow.y + ny * cy };
        return { l: { x: c.x - fm.u.x * w / 2, y: c.y - fm.u.y * w / 2 }, r: { x: c.x + fm.u.x * w / 2, y: c.y + fm.u.y * w / 2 } };
    }

    function marksFrom(fm, card) {
        var m = { r: { x: fm.rP.x, y: fm.rP.y }, l: { x: fm.lP.x, y: fm.lP.y }, mid: { x: fm.mid.x, y: fm.mid.y } };
        if (card) { m.cl = { x: card.l.x, y: card.l.y }; m.cr = { x: card.r.x, y: card.r.y }; }
        return m;
    }

    /* ═══ пересчёт по меткам ═══════════════════════════════════════════════ */

    function recalc() {
        var cur = S.cur;
        if (!cur || !cur.marks) return;
        var P = window.PDCore, fr = cur.frame, m = cur.marks;
        var ipd = dist(m.r, m.l);
        var u = { x: (m.l.x - m.r.x) / ipd, y: (m.l.y - m.r.y) / ipd };
        var monoR = (m.mid.x - m.r.x) * u.x + (m.mid.y - m.r.y) * u.y;
        monoR = clamp(monoR, ipd * 0.3, ipd * 0.7);
        var monoL = ipd - monoR;
        var D = cur.agg ? cur.agg.D : (cur.D || 450);
        var res;
        if (cur.scale === 'card') {
            if (!cur.edited && cur.multi) {
                var share = cur.multi.shareR;
                res = P.compute({ ipdPx: ipd, irisH: fr.fm.irisH, monoR: monoR, monoL: monoL, W: fr.W, H: fr.H, D: D, scale: 'card', cardPx: dist(m.cl, m.cr) });
                res.pd = cur.multi.pd; res.R = cur.multi.pd * share; res.L = cur.multi.pd - res.R;
                res.sd = cur.multi.sd; res.pm = P.plusMinus(cur.multi.sd); res.n = cur.multi.n;
            } else {
                res = P.compute({ ipdPx: ipd, irisH: fr.fm.irisH, monoR: monoR, monoL: monoL, W: fr.W, H: fr.H, D: D, scale: 'card', cardPx: dist(m.cl, m.cr), yaw: fr.fm.yaw });
            }
        } else {
            // По радужке: масштаб — медиана всего замера, а правка точек на этом
            // кадре меняет только отрезок между зрачками (в долях исходного).
            var a = cur.agg;
            var ratio = a ? a.ratio * (ipd / fr.fm.ipdPx) : ipd / fr.fm.irisH;
            var irisH = a ? a.irisH : fr.fm.irisH;
            var cal = S.calib && S.calib.hvid;
            // Без правки доля правого глаза — медиана всего замера, с правкой — по меткам.
            var shR = !cur.edited && a ? a.shareR : monoR / ipd;
            res = P.compute({
                ipdPx: ratio * irisH, irisH: irisH, monoR: shR, monoL: 1 - shR, W: fr.W, H: fr.H, D: D,
                scale: cal ? 'personal' : 'iris', hvidMm: cal || null, yaw: a ? a.yaw : fr.fm.yaw,
                noiseMm: a ? a.noiseRel * 63 : 0.8, frames: a ? a.n : 1,
            });
            res.n = a ? a.n : 1;
        }
        cur.res = res;
    }

    /* ═══ фото ═════════════════════════════════════════════════════════════ */

    async function onPhoto(file) {
        if (!file || !window.PDCore) return;
        hideErr();
        S.cur = null;
        setState('loading');
        busy(t('loading_photo'));
        stageMark('photo_loaded', { kb: Math.round(file.size / 1024) });
        var img;
        try { img = await loadImage(file); }
        catch (e) { setState('idle'); showErr(t('err_photo')); diag('pd_photo_decode', { msg: String(e && e.message).slice(0, 100), type: file.type }); return; }
        busy(window.FRLandmarks && window.FRLandmarks.loaded() ? t('busy_face') : t('loading_model'));
        try { await ensureDetector('IMAGE'); }
        catch (e) { setState('idle'); detectorFailed(e); return; }
        busy(t('busy_face'));
        await frameTick();
        var faces;
        try { faces = await window.FRLandmarks.detect(img.canvas, img.W, img.H); }
        catch (e) { setState('idle'); detectorFailed(e); return; }
        var face = faces && faces[0];
        var P = window.PDCore;
        var fm = face ? P.frameMetrics(face.pts, img.W, img.H, face.m) : null;
        if (!fm) { setState('idle'); showErr(t('photo_no_face')); ev('pd_photo_noface', {}); return; }
        var fr = { canvas: img.canvas, W: img.W, H: img.H, fm: fm, pts: face.pts };
        // Дистанцию съёмки фото не узнать: снимок могли обрезать или снять
        // с зумом. Берём типичное селфи с вытянутой руки — 45 см; поправка на
        // сведение глаз от этого зависит слабо (±0.5 мм на ±15 см).
        var D = 450;
        busy(t('busy_card'));
        await frameTick();
        var card = findCard(fr);
        var q = P.frameQuality(fm, { hfov: 70, nearMm: 150, farMm: 2000, minIrisPx: 10 });
        var cur = { src: 'photo', mirror: false, frame: fr, D: D, edited: false, quality: q };
        if (card && card.ok) {
            cur.scale = 'card';
            cur.card = { l: card.left, r: card.right, auto: true, contrast: card.contrast };
        } else {
            cur.scale = 'iris';
            cur.card = null;
        }
        cur.agg = { ratio: fm.ipdPx / fm.irisH, irisH: fm.irisH, shareR: fm.monoR / (fm.monoR + fm.monoL), D: D, yaw: fm.yaw, n: 1, noiseRel: 0 };
        cur.marks = marksFrom(fm, cur.card);
        cur.orig = JSON.parse(JSON.stringify(cur.marks));
        S.cur = cur;
        S.zoom = true;
        syncZoomBtn();
        recalc();
        setState('result');
        renderResult();
        pushRun();
        ev('pd_photo_result', { scale: cur.scale, card: card ? (card.ok ? 1 : 0) : -1, iris: Math.round(fm.irisH), yaw: Math.round(fm.yaw) });
        try { if (window.__aeToolComplete) window.__aeToolComplete({ mode: 'photo', scale: cur.scale, pd: Math.round(cur.res.pd * 10) / 10, pm: cur.res.pm }); } catch (_) { }
    }

    async function loadImage(file) {
        var bmp = null;
        try { bmp = await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch (_) { bmp = null; }
        var src = bmp, w, h;
        if (!bmp) {
            var url = URL.createObjectURL(file);
            try {
                src = await new Promise(function (res, rej) { var im = new Image(); im.onload = function () { res(im); }; im.onerror = rej; im.src = url; });
            } finally { setTimeout(function () { URL.revokeObjectURL(url); }, 5000); }
            w = src.naturalWidth; h = src.naturalHeight;
        } else { w = bmp.width; h = bmp.height; }
        if (!w || !h) throw new Error('empty image');
        var sc = Math.min(1, 2600 / Math.max(w, h));
        var c = document.createElement('canvas');
        c.width = Math.round(w * sc); c.height = Math.round(h * sc);
        var cx = c.getContext('2d', { willReadFrequently: true });
        cx.imageSmoothingQuality = 'high';
        cx.drawImage(src, 0, 0, c.width, c.height);
        try { if (bmp && bmp.close) bmp.close(); } catch (_) { }
        return { canvas: c, W: c.width, H: c.height };
    }

    /* ═══ «знаю PD» ════════════════════════════════════════════════════════ */

    function onKnownInput() {
        var raw = $('pdKnownIn').value;
        var p = window.PDCore && window.PDCore.parsePrescription(raw);
        var hint = $('pdKnownHint');
        if (!p || p.pd < 40 || p.pd > 85) {
            S.known = null;
            hint.textContent = raw.trim() ? t('known_err') : t('known_hint');
            hint.style.color = raw.trim() ? 'var(--lg-warn)' : '';
            renderGlasses(null);
            if (S.state === 'result') setState('idle');
            return;
        }
        hint.textContent = p.mono ? t('known_mono', { r: f1(p.R), l: f1(p.L) }) : (p.pair ? t('known_pair', { far: f1(p.pd), near: f1(p.near) }) : t('known_hint'));
        hint.style.color = '';
        S.known = { pd: p.pd, R: p.R || p.pd / 2, L: p.L || p.pd / 2, near: p.near || null, mono: !!p.mono, scale: 'known', pm: null };
        renderGlasses(S.known);
        if (S.state !== 'result') setState('result');
        renderResult();
        ev('pd_known', { mono: p.mono ? 1 : 0, pair: p.pair ? 1 : 0 });
    }

    function renderGlasses(k) {
        $('pdGlPd').textContent = k ? fh(k.pd) + ' ' + t('mm') : 'PD';
        $('pdGlR').textContent = k ? 'OD ' + fh(k.R) : 'OD';
        $('pdGlL').textContent = k ? 'OS ' + fh(k.L) : 'OS';
        // Центры линз двигаются вместе с числами — чтобы асимметрия была видна.
        var share = k ? clamp(k.R / k.pd, 0.4, 0.6) : 0.5;
        var span = 156, x0 = 170 - span * share, x1 = x0 + span;
        $('pdGlOcR').setAttribute('cx', x0.toFixed(1));
        $('pdGlOcL').setAttribute('cx', x1.toFixed(1));
        $('pdGlDim').setAttribute('d', 'M' + x0 + ' 30v48M' + x1 + ' 30v48M' + x0 + ' 36h' + (x1 - x0));
        $('pdGlDimR').setAttribute('d', 'M' + x0 + ' 140h' + (170 - x0) + 'M' + x0 + ' 134v12');
        $('pdGlDimL').setAttribute('d', 'M170 140h' + (x1 - 170) + 'M' + x1 + ' 134v12');
        $('pdGlR').setAttribute('x', ((x0 + 170) / 2).toFixed(1));
        $('pdGlL').setAttribute('x', ((x1 + 170) / 2).toFixed(1));
    }

    /* ═══ результат ════════════════════════════════════════════════════════ */

    function current() {
        if (S.mode === 'known') return S.known;
        return S.cur && S.cur.res ? S.cur.res : null;
    }

    function combined() {
        var r = current();
        if (!r || S.mode === 'known') return r ? Object.assign({ n: 1 }, r) : null;
        var runs = S.runs.filter(function (x) { return x.scale === (S.cur && S.cur.scale); });
        if (runs.length < 2) return Object.assign({ runN: runs.length || 1 }, r);
        var c = window.PDCore.combine(runs.map(function (x) { return x.res; }));
        return Object.assign({}, r, { pd: c.pd, R: c.R, L: c.L, sd: c.sd, pm: c.pm, runN: c.n, spread: c.spread });
    }

    function renderResult() {
        var P = window.PDCore;
        var r = combined();
        if (!r) return;
        var known = S.mode === 'known';
        var cur = S.cur;
        var scale = known ? 'known' : (cur.scale === 'iris' && S.calib ? 'personal' : cur.scale);
        var cardMissing = !known && cur.scale === 'card' && cur.card && !cur.card.auto && !cur.edited;

        $('pdFar').textContent = known ? f1(r.pd) : fh(r.pd);
        $('pdPm').textContent = known || !r.pm ? '' : t('res_pm', { mm: f1(r.pm) });
        var badge = $('pdBadge');
        badge.className = 'pd-badge ' + scale;
        badge.textContent = t('badge_' + scale);

        // Где число на шкале взрослых (5–95-й перцентиль: 55.5–70 мм)
        var lo = 48, hi = 78;
        var pos = function (v) { return (clamp(v, lo, hi) - lo) / (hi - lo) * 100; };
        $('pdRangeBand').style.left = pos(55.5) + '%';
        $('pdRangeBand').style.width = (pos(70) - pos(55.5)) + '%';
        $('pdRangeMk').style.left = pos(r.pd) + '%';
        $('pdRangeLo').textContent = nf.format(lo);
        $('pdRangeHi').textContent = nf.format(hi);

        var near = r.near && known ? { pd: r.near, R: r.near * r.R / r.pd, L: r.near * r.L / r.pd } : P.nearFrom(r, S.reading);
        var approx = !known && monoYaw(cur) > 5 ? '≈' : '';
        $('pdR').textContent = approx + fh(r.R);
        $('pdL').textContent = approx + fh(r.L);
        $('pdNear').textContent = fh(near.pd);
        $('pdNearL').textContent = known && r.near ? t('res_near_given') : t('res_near', { cm: Math.round(S.reading / 10) });

        // Три записи, которые спрашивают при заказе, — каждая копируется нажатием.
        var codes = [
            { k: 'code_mono', v: fh(r.R) + ' / ' + fh(r.L) },
            { k: 'code_pair', v: fh(r.pd) + ' / ' + fh(near.pd) },
        ];
        var box = $('pdCodes');
        box.textContent = '';
        codes.forEach(function (c) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'pd-code';
            b.title = t('copy_hint');
            var sm = document.createElement('small'); sm.textContent = t(c.k);
            b.appendChild(sm);
            b.appendChild(document.createTextNode(c.v));
            b.addEventListener('click', function () { copyText(c.v, c.k); });
            box.appendChild(b);
        });

        var readB = P.readyPick(near.pd, 'reading'), farB = P.readyPick(r.pd, 'far');
        var ready = $('pdReady');
        ready.textContent = '';
        ready.appendChild(document.createTextNode(t('ready_lead') + ' '));
        [[t('ready_far'), bin(farB)], [t('ready_read'), bin(readB)]].forEach(function (p, i) {
            if (i) ready.appendChild(document.createTextNode(' · '));
            ready.appendChild(document.createTextNode(p[0] + ' '));
            var b = document.createElement('b'); b.textContent = p[1]; ready.appendChild(b);
        });

        renderMsg(r, cur, known, cardMissing);
        renderRuns(r, known);
        $('pdAgain').hidden = known;
        // После замера без карты следующий правильный шаг — замер с картой:
        // он и становится главной кнопкой.
        S.againCard = !known && cur && cur.scale === 'iris' && !S.calib;
        $('pdAgain').textContent = S.againCard ? t('btn_with_card') : t('btn_again');
        $('pdShare').hidden = !(navigator.share);
        renderDrawers(r, near);
        renderReviewHint();
        renderGlasses(known ? r : null);
        requestAnimationFrame(redraw);
    }

    // Деление на OD/OS зависит от поворота головы (зрачки смещаются в
    // глазницах), и поправка по оценке позы MediaPipe — не лучше самой оценки.
    // Больше 5° — честно говорим, что монокулярные значения приблизительны.
    function monoYaw(cur) {
        if (!cur) return 0;
        var y = cur.agg ? cur.agg.yaw : (cur.frame && cur.frame.fm ? cur.frame.fm.yaw : 0);
        return Math.abs(y || 0);
    }

    function renderMsg(r, cur, known, cardMissing) {
        var msg = $('pdMsg');
        msg.textContent = '';
        msg.className = 'tc-msg';
        var text = null, kind = '', action = null;
        if (cardMissing) { text = t('card_not_found'); kind = 'bad'; }
        else if (r.pd < window.PDCore.C.ADULT_MIN || r.pd > window.PDCore.C.ADULT_MAX) { text = t('msg_range'); kind = 'warn'; }
        else if (!known && cur && cur.src === 'photo' && cur.quality && !cur.quality.ok && ['turn', 'tilt', 'gaze'].indexOf(cur.quality.issue) >= 0) {
            text = t('msg_photo_pose'); kind = 'warn';
        }
        else if (!known && cur && cur.scale === 'iris' && !S.calib) {
            // Действие «замерить с картой» — главная кнопка результата (см. renderResult).
            text = t('msg_iris'); kind = 'warn';
        }
        else if (!known && monoYaw(cur) > 5) { text = t('msg_mono_yaw', { deg: Math.round(monoYaw(cur)) }); kind = 'warn'; }
        else if (Math.abs(r.R - r.L) > 2.5) { text = t('msg_asym', { mm: f1(Math.abs(r.R - r.L)) }); kind = 'warn'; }
        else if (!known && cur && cur.edited) { text = t('msg_edited'); }
        if (!text) { msg.hidden = true; return; }
        msg.hidden = false;
        if (kind) msg.classList.add(kind);
        msg.appendChild(document.createTextNode(text));
        if (action) {
            var d = document.createElement('div'); d.className = 'pd-msg-act';
            var b = document.createElement('button'); b.type = 'button'; b.className = 'tc-btn sm'; b.textContent = action.label;
            b.addEventListener('click', action.fn);
            d.appendChild(b); msg.appendChild(d);
        }
    }

    function renderRuns(r, known) {
        var el = $('pdRuns');
        el.className = 'pd-runs';
        if (known) { el.textContent = ''; el.hidden = true; return; }
        el.hidden = false;
        var n = r.runN || 1;
        if (n < 2) { el.textContent = t('runs_one'); return; }
        if (r.spread > 2) { el.textContent = t('runs_bad', { n: n, mm: f1(r.spread) }); return; }
        el.textContent = t('runs_n', { n: n, mm: f1(r.spread) });
        el.classList.add('good');
    }

    function renderReviewHint() {
        var h = $('pdReviewHint');
        var cur = S.cur;
        if (!cur || !cur.frame) return;
        var cardMissing = cur.scale === 'card' && cur.card && !cur.card.auto && !cur.edited;
        h.classList.toggle('warn', !!cardMissing);
        h.textContent = cardMissing ? t('review_hint_card_missing') : (cur.scale === 'card' ? t('review_hint_card') : t('review_hint'));
        $('pdResetPts').hidden = !cur.edited;
        var rl = cur.src === 'photo' ? t('btn_other_photo') : t('btn_retake');
        $('pdRetake').title = rl;
        $('pdRetake').setAttribute('aria-label', rl);
    }

    function renderDrawers(r, near) {
        var P = window.PDCore;
        var nt = $('pdNearTable').tBodies[0];
        nt.textContent = '';
        [[330, 'near_33'], [400, 'near_40'], [500, 'near_50'], [650, 'near_65']].forEach(function (row) {
            var n = P.nearFrom(r, row[0]);
            var tr = document.createElement('tr');
            var a = document.createElement('td'); a.textContent = t(row[1]);
            var b = document.createElement('td'); b.className = 'v';
            b.textContent = fh(n.pd) + ' ' + t('mm');
            var sm = document.createElement('small'); sm.textContent = 'OD ' + fh(n.R) + ' · OS ' + fh(n.L);
            b.appendChild(sm);
            tr.appendChild(a); tr.appendChild(b);
            if (row[0] === S.reading) tr.className = 'now';
            tr.style.cursor = 'pointer';
            tr.title = t('near_pick');
            tr.addEventListener('click', function () { S.reading = row[0]; try { window.__toolSettings && window.__toolSettings.save(SLUG, 'reading', row[0]); } catch (_) { } renderResult(); ev('pd_reading', { mm: row[0] }); });
            nt.appendChild(tr);
        });
        var vt = $('pdVrTable').tBodies[0];
        vt.textContent = '';
        P.vrSettings(r.pd).forEach(function (h) {
            var tr = document.createElement('tr');
            var a = document.createElement('td'); a.textContent = h.name;
            var b = document.createElement('td'); b.className = 'v';
            var main, sub = '';
            if (h.kind === 'range') {
                main = fh(h.value) + ' ' + t('mm');
                if (h.clamped) sub = t(h.clamped === 'min' ? 'vr_out_min' : 'vr_out_max', { min: h.min, max: h.max });
            } else if (h.kind === 'steps') { main = t('vr_step', { n: h.step, v: h.value }); }
            else if (h.kind === 'guided') { main = t('vr_guided'); if (h.clamped) sub = t(h.clamped === 'min' ? 'vr_out_min' : 'vr_out_max', { min: h.min, max: h.max }); }
            else { main = t('vr_auto'); if (h.clamped) sub = t(h.clamped === 'min' ? 'vr_out_min' : 'vr_out_max', { min: h.min, max: h.max }); }
            b.textContent = main;
            if (sub) { var sm = document.createElement('small'); sm.textContent = sub; b.appendChild(sm); }
            tr.appendChild(a); tr.appendChild(b);
            vt.appendChild(tr);
        });
        renderRx();
        renderHistory();
    }

    /* ── советник точности: правило Прентиса + ANSI Z80.1 ───────────────── */
    function renderRx() {
        var r = combined();
        var out = $('pdRxVerdict');
        var raw = String($('pdRxPower').value || '').replace(/[−–—]/g, '-').replace(',', '.').replace(/[^\d.+-]/g, '');
        var F = parseFloat(raw);
        var prog = $('pdRxProg').checked;
        if (!r || (!isFinite(F) && !prog)) { out.hidden = true; return; }
        out.hidden = false;
        out.className = 'tc-msg';
        var pm = r.pm || 0;
        if (prog) {
            out.textContent = t('rx_prog_verdict');
            out.classList.add('warn');
            return;
        }
        var a = Math.abs(F);
        // До ±2.75 дптр стандарт считает призму (0.67 Δ на пару → 6.7/F мм);
        // выше — просто ±2.5 мм по PD.
        var tol = a <= 2.75 ? (a < 0.25 ? 10 : Math.min(10, 6.7 / a)) : 2.5;
        var pw;
        try { pw = new Intl.NumberFormat(LOC === 'ar' ? 'ar-u-nu-latn' : LOC, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(F)); }
        catch (_) { pw = Math.abs(F).toFixed(2); }
        pw = (F > 0 ? '+' : F < 0 ? '−' : '') + pw;
        if (S.mode === 'known' || !pm) {
            out.textContent = t('rx_tol', { power: pw, tol: f1(tol) });
            return;
        }
        var ok = pm <= tol;
        out.textContent = t(ok ? 'rx_ok' : 'rx_no', { power: pw, tol: f1(tol), pm: f1(pm) });
        out.classList.add(ok ? 'good' : 'warn');
    }

    /* ── история (в этом браузере) ─────────────────────────────────────── */
    var HKEY = 'pd_history_v1';
    function histLoad() { try { return JSON.parse(localStorage.getItem(HKEY) || '[]') || []; } catch (_) { return []; } }
    function histSave(a) { try { localStorage.setItem(HKEY, JSON.stringify(a.slice(0, 12))); } catch (_) { } }
    function pushRun() {
        var cur = S.cur;
        if (!cur || !cur.res) return;
        S.runs.push({ scale: cur.scale, res: Object.assign({}, cur.res), src: cur.src });
        var c = combined();
        var h = histLoad();
        var entry = { t: Date.now(), pd: +c.pd.toFixed(2), R: +c.R.toFixed(2), L: +c.L.toFixed(2), pm: c.pm, sc: cur.scale, src: cur.src, n: c.runN || 1, sid: sessionId };
        if (h.length && h[0].sid === sessionId && h[0].sc === entry.sc) h[0] = entry; else h.unshift(entry);
        histSave(h);
    }
    var sessionId = Math.random().toString(36).slice(2, 10);
    function renderHistory() {
        var h = histLoad();
        var ul = $('pdHist');
        ul.textContent = '';
        var df;
        try { df = new Intl.DateTimeFormat(LOC, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (_) { df = null; }
        h.forEach(function (e) {
            var li = document.createElement('li');
            var b = document.createElement('b'); b.textContent = fh(e.pd) + ' ' + t('mm');
            var s = document.createElement('span'); s.textContent = 'OD ' + fh(e.R) + ' · OS ' + fh(e.L) + ' · ' + t('hist_' + (e.src === 'photo' ? 'photo' : 'camera') + '_' + e.sc) + (e.n > 1 ? ' ×' + e.n : '');
            var d = document.createElement('span'); d.className = 'pd-hist-d'; d.textContent = df ? df.format(new Date(e.t)) : '';
            li.appendChild(b); li.appendChild(s); li.appendChild(d);
            ul.appendChild(li);
        });
        $('pdHistEmpty').hidden = h.length > 0;
        $('pdHistClear').hidden = !h.length;
        $('pdHistCount').textContent = h.length ? String(h.length) : '';
    }

    /* ── калибровка своей радужки ──────────────────────────────────────── */
    var CKEY = 'pd_iris_calib_v1';
    function loadCalib() {
        try { var c = JSON.parse(localStorage.getItem(CKEY) || 'null'); if (c && c.hvid > 9.5 && c.hvid < 13.8) S.calib = c; } catch (_) { }
        syncCalib();
    }
    function saveCalib(hvid) {
        if (!(hvid > 9.5 && hvid < 13.8)) return;
        S.calib = { hvid: hvid, t: Date.now() };
        try { localStorage.setItem(CKEY, JSON.stringify(S.calib)); } catch (_) { }
        syncCalib();
        ev('pd_calib_saved', { hvid: Math.round(hvid * 100) / 100 });
    }
    function syncCalib() {
        var c = S.calib;
        $('pdCalib').hidden = !c;
        if (c) $('pdCalibText').textContent = t('calib_on', { mm: f1(c.hvid) });
        syncScaleSub();
    }

    /* ── копировать / поделиться / PDF ─────────────────────────────────── */
    function summary() {
        var r = combined();
        if (!r) return '';
        var near = r.near && S.mode === 'known' ? r.near : window.PDCore.nearFrom(r, S.reading).pd;
        return t('share_text', { pd: fh(r.pd), r: fh(r.R), l: fh(r.L), near: fh(near), cm: Math.round(S.reading / 10) });
    }
    function copyText(text, what) {
        var done = function () { toast(t('copied'), 'success'); ev('pd_copy', { what: what || 'all' }); };
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(text).then(done, function () { fallbackCopy(text) && done(); }); return; }
        } catch (_) { }
        if (fallbackCopy(text)) done();
    }
    function fallbackCopy(text) {
        try {
            var ta = document.createElement('textarea');
            ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.left = '-9999px';
            document.body.appendChild(ta); ta.select();
            var ok = document.execCommand('copy');
            document.body.removeChild(ta);
            return ok;
        } catch (_) { return false; }
    }
    function share() {
        var text = summary();
        if (!navigator.share) { copyText(text, 'share_fallback'); return; }
        navigator.share({ title: t('pdf_title'), text: text, url: location.href.split('?')[0] }).then(function () { ev('pd_share', {}); }, function () { });
    }

    function methodLabel() {
        if (S.mode === 'known') return t('method_known');
        var c = S.cur;
        if (!c) return '';
        var key = (c.src === 'photo' ? 'method_photo_' : 'method_camera_') + (c.scale === 'iris' && S.calib ? 'personal' : c.scale);
        return t(key);
    }

    function makePdf() {
        var r = combined();
        if (!r) return;
        var near = r.near && S.mode === 'known' ? { pd: r.near, R: r.near * r.R / r.pd, L: r.near * r.L / r.pd } : window.PDCore.nearFrom(r, S.reading);
        var el = document.createElement('div');
        el.className = 'pd-pdf';
        var date;
        try { date = new Intl.DateTimeFormat(LOC, { dateStyle: 'long' }).format(new Date()); } catch (_) { date = window.__localYmd(); }
        var rows = [
            [t('res_far'), fh(r.pd) + ' ' + t('mm') + (r.pm && S.mode !== 'known' ? '  (' + t('res_pm', { mm: f1(r.pm) }) + ')' : '')],
            [t('res_right'), fh(r.R) + ' ' + t('mm')],
            [t('res_left'), fh(r.L) + ' ' + t('mm')],
            [t('res_near', { cm: Math.round(S.reading / 10) }), fh(near.pd) + ' ' + t('mm') + '  (OD ' + fh(near.R) + ' · OS ' + fh(near.L) + ')'],
            [t('code_pair'), fh(r.pd) + ' / ' + fh(near.pd)],
            [t('pdf_method'), methodLabel()],
            [t('pdf_date'), date],
        ];
        var html = '<div style="padding:40px 44px;font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#0f172a">'
            + '<div style="font-size:13px;letter-spacing:.08em;text-transform:uppercase;color:#64748b;font-weight:700">' + esc(t('pdf_kicker')) + '</div>'
            + '<div style="font-size:26px;font-weight:800;margin:6px 0 18px">' + esc(t('pdf_title')) + '</div>'
            + '<div style="display:flex;align-items:baseline;gap:10px;margin-bottom:18px"><span style="font-size:64px;font-weight:800;color:#2f5fe0;line-height:1">' + esc(fh(r.pd)) + '</span><span style="font-size:22px;color:#64748b;font-weight:700">' + esc(t('mm')) + '</span></div>'
            + '<table style="width:100%;border-collapse:collapse;font-size:15px">'
            + rows.map(function (rw) { return '<tr><td style="padding:9px 0;border-bottom:1px solid #e2e8f0;color:#475569">' + esc(rw[0]) + '</td><td style="padding:9px 0;border-bottom:1px solid #e2e8f0;text-align:right;font-weight:700">' + esc(rw[1]) + '</td></tr>'; }).join('')
            + '</table>'
            + '<p style="margin:18px 0 0;font-size:12.5px;line-height:1.55;color:#475569">' + esc(t('disclaimer')) + '</p>'
            + '<p style="margin:10px 0 0;font-size:12px;color:#94a3b8">' + esc(location.host + location.pathname) + '</p>'
            + '</div>';
        el.innerHTML = html;
        document.body.appendChild(el);
        var run = function () {
            window.__generatePDF({
                element: el, filename: 'pd-' + window.__localYmd() + '.pdf',
                strings: { saved: t('pdf_saved') },
            }).then(function (res) { ev('pd_pdf', { ok: res && res.ok ? 1 : 0 }); })
              .finally(function () { try { el.remove(); } catch (_) { } });
        };
        if (window.__generatePDF) run();
        else { toast(t('pdf_wait'), 'info'); whenReady(['__generatePDF'], function (miss) { if (miss) { el.remove(); toast(t('pdf_fail'), 'error'); } else run(); }); }
    }
    function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

    /* ═══ сцена: рисование ═════════════════════════════════════════════════ */

    var cv = $('pdCanvas');
    function sizeCanvas() {
        var r = cv.getBoundingClientRect();
        var dpr = Math.min(window.devicePixelRatio || 1, 2.5);
        var w = Math.max(1, Math.round(r.width * dpr)), h = Math.max(1, Math.round(r.height * dpr));
        if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
        return { w: r.width, h: r.height, dpr: dpr };
    }

    // Отображение кадра в канву: cover (живое видео) или вид-обрезка (правка).
    function coverMap(W, H, cw, ch, mirror) {
        var s = Math.max(cw / W, ch / H), dw = W * s, dh = H * s, ox = (cw - dw) / 2, oy = (ch - dh) / 2;
        return {
            s: s,
            x: function (x) { return mirror ? ox + (W - x) * s : ox + x * s; },
            y: function (y) { return oy + y * s; },
        };
    }

    function drawLive(fm, W, H, now) {
        var z = sizeCanvas();
        var ctx = cv.getContext('2d');
        ctx.setTransform(z.dpr, 0, 0, z.dpr, 0, 0);
        ctx.clearRect(0, 0, z.w, z.h);
        var M = coverMap(W, H, z.w, z.h, true);
        if (!fm) {
            // овал, куда поставить лицо
            ctx.save();
            ctx.strokeStyle = 'rgba(255,255,255,.75)';
            ctx.setLineDash([7, 6]);
            ctx.lineWidth = 2;
            var rx = Math.min(z.w, z.h) * 0.24, ry = rx * 1.3;
            ctx.beginPath(); ctx.ellipse(z.w / 2, z.h / 2, rx, ry, 0, 0, Math.PI * 2); ctx.stroke();
            ctx.restore();
            return;
        }
        var P = window.PDCore;
        // карта: где она найдена (зелёный) или где её ждут (пунктир)
        if (S.scale === 'card' && S.live) {
            var box = S.live.cardBox && (now - S.live.cardSeenAt < 900) ? S.live.cardBox.corners : null;
            if (!box) {
                var e = expectedCard(fm, P.distanceMm(fm, S.hfov));
                var hPx = dist(e.l, e.r) * 53.98 / 85.6, nx = fm.u.y, ny = -fm.u.x;
                box = [
                    { x: e.l.x - nx * hPx / 2, y: e.l.y - ny * hPx / 2 }, { x: e.r.x - nx * hPx / 2, y: e.r.y - ny * hPx / 2 },
                    { x: e.r.x + nx * hPx / 2, y: e.r.y + ny * hPx / 2 }, { x: e.l.x + nx * hPx / 2, y: e.l.y + ny * hPx / 2 },
                ];
                ctx.setLineDash([8, 6]);
                ctx.strokeStyle = 'rgba(245,165,36,.95)';
                ctx.fillStyle = 'rgba(245,165,36,.10)';
            } else {
                ctx.setLineDash([]);
                ctx.strokeStyle = 'rgba(52,211,153,.95)';
                ctx.fillStyle = 'rgba(52,211,153,.12)';
            }
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            box.forEach(function (p, i) { var x = M.x(p.x), y = M.y(p.y); if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y); });
            ctx.closePath(); ctx.fill(); ctx.stroke();
            ctx.setLineDash([]);
        }
        // радужки и зрачки
        [fm.rP, fm.lP].forEach(function (p) {
            var x = M.x(p.x), y = M.y(p.y), rr = fm.irisH / 2 * M.s;
            ctx.strokeStyle = 'rgba(56,189,248,.95)'; ctx.lineWidth = 1.6;
            ctx.beginPath(); ctx.arc(x, y, rr, 0, Math.PI * 2); ctx.stroke();
            ctx.fillStyle = '#38bdf8';
            ctx.beginPath(); ctx.arc(x, y, 2.4, 0, Math.PI * 2); ctx.fill();
        });
        // размерная линия ПОД глазами (над ними — карта) с живым числом
        var dn = fm.irisH * 1.5;
        var ax = M.x(fm.rP.x - fm.u.y * dn), ay = M.y(fm.rP.y + dn * fm.u.x);
        var bx = M.x(fm.lP.x - fm.u.y * dn), by = M.y(fm.lP.y + dn * fm.u.x);
        ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.lineWidth = 1.4;
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by);
        ctx.moveTo(ax, ay - 5); ctx.lineTo(ax, ay + 5); ctx.moveTo(bx, by - 5); ctx.lineTo(bx, by + 5); ctx.stroke();
        var live = P.compute({ ipdPx: fm.ipdPx, irisH: fm.irisH, monoR: fm.monoR, monoL: fm.monoL, W: W, H: H, scale: S.calib ? 'personal' : 'iris', hvidMm: S.calib ? S.calib.hvid : null, hfov: S.hfov, yaw: fm.yaw });
        label(ctx, '≈ ' + fh(live.pd) + ' ' + t('mm'), (ax + bx) / 2, (ay + by) / 2 + 16);
    }

    function label(ctx, text, x, y, color) {
        ctx.save();
        ctx.font = '700 13px system-ui,-apple-system,Segoe UI,Roboto,sans-serif';
        var w = ctx.measureText(text).width + 14;
        ctx.fillStyle = 'rgba(6,10,22,.78)';
        roundRect(ctx, x - w / 2, y - 12, w, 22, 11); ctx.fill();
        ctx.fillStyle = color || '#fff';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(text, x, y - 1);
        ctx.restore();
    }
    function roundRect(ctx, x, y, w, h, r) {
        ctx.beginPath();
        ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
    }

    // Вид на кадр при правке: крупно — полоса глаз (и карта), целиком — весь кадр.
    function viewFor(cur, cw, ch) {
        var fr = cur.frame, m = cur.marks;
        var ipd = dist(m.r, m.l);
        var sw, sh, cx, cy;
        if (S.zoom) {
            // Рамка вида: сверху — верх карты (если она есть), снизу — запас под
            // глазами, по бокам — оба зрачка и края карты с полями.
            var ey = (m.r.y + m.l.y) / 2;
            var top = ey - ipd * 0.55, bot = ey + ipd * 0.5;
            var left = Math.min(m.r.x, m.l.x) - ipd * 0.55, right = Math.max(m.r.x, m.l.x) + ipd * 0.55;
            if (m.cl) {
                var cw0 = dist(m.cl, m.cr), cmy = (m.cl.y + m.cr.y) / 2;
                top = Math.min(top, cmy - cw0 * 0.63 / 2 - ipd * 0.3);
                left = Math.min(left, Math.min(m.cl.x, m.cr.x) - ipd * 0.2);
                right = Math.max(right, Math.max(m.cl.x, m.cr.x) + ipd * 0.2);
            }
            sw = right - left; sh = bot - top;
            if (sw / sh < cw / ch) sw = sh * cw / ch; else sh = sw * ch / cw;
            cx = (left + right) / 2; cy = (top + bot) / 2;
        } else {
            var s0 = Math.min(cw / fr.W, ch / fr.H);
            sw = cw / s0; sh = ch / s0; cx = fr.W / 2; cy = fr.H / 2;
        }
        if (S.zoom) {
            if (sw > fr.W) { sw = fr.W; sh = sw * ch / cw; }
            if (sh > fr.H) { sh = fr.H; sw = sh * cw / ch; }
            cx = clamp(cx, sw / 2, fr.W - sw / 2);
            cy = clamp(cy, sh / 2, fr.H - sh / 2);
        }
        var sx = cx - sw / 2, sy = cy - sh / 2, s = cw / sw;
        var mir = !!cur.mirror;
        return {
            sx: sx, sy: sy, sw: sw, sh: sh, s: s, mirror: mir,
            x: function (x) { return mir ? (sx + sw - x) * s : (x - sx) * s; },
            y: function (y) { return (y - sy) * s; },
            ix: function (dx) { return mir ? sx + sw - dx / s : sx + dx / s; },
            iy: function (dy) { return sy + dy / s; },
        };
    }

    function redraw() {
        if (S.state === 'result' && S.cur && S.cur.frame && S.mode !== 'known') drawReview();
    }

    function drawReview() {
        var cur = S.cur, fr = cur.frame, m = cur.marks;
        var z = sizeCanvas();
        if (z.w < 2 || z.h < 2) return;
        var ctx = cv.getContext('2d');
        ctx.setTransform(z.dpr, 0, 0, z.dpr, 0, 0);
        ctx.fillStyle = '#05070d';
        ctx.fillRect(0, 0, z.w, z.h);
        var V = viewFor(cur, z.w, z.h);
        S.view = V;
        ctx.save();
        ctx.imageSmoothingQuality = 'high';
        if (V.mirror) { ctx.translate(z.w, 0); ctx.scale(-1, 1); }
        var dx = 0, dy = 0, dw = z.w, dh = z.h;
        // кадр может быть меньше вида (режим «целиком») — рисуем по месту
        var sx0 = Math.max(0, V.sx), sy0 = Math.max(0, V.sy);
        var sx1 = Math.min(fr.W, V.sx + V.sw), sy1 = Math.min(fr.H, V.sy + V.sh);
        dx = (sx0 - V.sx) * V.s; dy = (sy0 - V.sy) * V.s; dw = (sx1 - sx0) * V.s; dh = (sy1 - sy0) * V.s;
        ctx.drawImage(fr.canvas, sx0, sy0, sx1 - sx0, sy1 - sy0, dx, dy, dw, dh);
        ctx.restore();

        var sel = S.drag ? S.drag.key : S.sel;
        // карта
        if (m.cl) {
            var ok = cur.card && (cur.card.auto || cur.edited);
            var col = ok ? '#f5a524' : '#f87171';
            var a = { x: V.x(m.cl.x), y: V.y(m.cl.y) }, b = { x: V.x(m.cr.x), y: V.y(m.cr.y) };
            var hh = dist(a, b) * 53.98 / 85.6 * 0.5;
            ctx.strokeStyle = col; ctx.lineWidth = 2;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
            [['cl', a], ['cr', b]].forEach(function (p) {
                ctx.lineWidth = p[0] === sel ? 3.5 : 2.5;
                ctx.beginPath(); ctx.moveTo(p[1].x, p[1].y - hh); ctx.lineTo(p[1].x, p[1].y + hh); ctx.stroke();
                ctx.fillStyle = col;
                ctx.beginPath(); ctx.arc(p[1].x, p[1].y, p[0] === sel ? 8 : 6.5, 0, Math.PI * 2); ctx.fill();
                ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke(); ctx.strokeStyle = col;
            });
            label(ctx, t('card_len'), (a.x + b.x) / 2, (a.y + b.y) / 2 - 14, ok ? '#ffd48a' : '#ffb4b4');
        }
        // середина переносицы
        var mx = V.x(m.mid.x), my = V.y(m.mid.y);
        ctx.save();
        ctx.strokeStyle = 'rgba(167,139,250,.95)'; ctx.setLineDash([4, 4]); ctx.lineWidth = 1.5;
        var ipdD = dist({ x: V.x(m.r.x), y: V.y(m.r.y) }, { x: V.x(m.l.x), y: V.y(m.l.y) });
        ctx.beginPath(); ctx.moveTo(mx, my - ipdD * 0.35); ctx.lineTo(mx, my + ipdD * 0.35); ctx.stroke();
        ctx.restore();
        ctx.fillStyle = '#a78bfa';
        ctx.beginPath(); ctx.moveTo(mx, my - 7); ctx.lineTo(mx + 7, my); ctx.lineTo(mx, my + 7); ctx.lineTo(mx - 7, my); ctx.closePath(); ctx.fill();
        if (sel === 'mid') { ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke(); }
        // зрачки
        var res = cur.res;
        [['r', m.r, t('mk_r')], ['l', m.l, t('mk_l')]].forEach(function (p) {
            var x = V.x(p[1].x), y = V.y(p[1].y);
            var rr = Math.max(10, (cur.frame.fm.irisH / 2) * V.s);
            ctx.strokeStyle = p[0] === sel ? '#ffffff' : '#38bdf8';
            ctx.lineWidth = p[0] === sel ? 2.2 : 1.6;
            ctx.beginPath(); ctx.arc(x, y, rr, 0, Math.PI * 2); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(x - rr - 5, y); ctx.lineTo(x - 3, y); ctx.moveTo(x + 3, y); ctx.lineTo(x + rr + 5, y);
            ctx.moveTo(x, y - rr - 5); ctx.lineTo(x, y - 3); ctx.moveTo(x, y + 3); ctx.lineTo(x, y + rr + 5); ctx.stroke();
            label(ctx, p[2] + (res ? ' ' + fh(p[0] === 'r' ? res.R : res.L) : ''), x, y + rr + 20);
        });
        if (res) {
            var ax = V.x(m.r.x), ay = V.y(m.r.y), bx = V.x(m.l.x), by = V.y(m.l.y);
            var lift = Math.max(26, cur.frame.fm.irisH * V.s * 1.1);
            ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.lineWidth = 1.4;
            ctx.beginPath(); ctx.moveTo(ax, ay - lift); ctx.lineTo(bx, by - lift); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(ax, ay - lift - 5); ctx.lineTo(ax, ay - lift + 5); ctx.moveTo(bx, by - lift - 5); ctx.lineTo(bx, by - lift + 5); ctx.stroke();
            label(ctx, 'PD ' + fh(res.pd) + ' ' + t('mm'), (ax + bx) / 2, (ay + by) / 2 - lift - 14, '#9be3ff');
        }
    }

    /* ── правка меток: перетаскивание, лупа, стрелки ───────────────────── */

    var KEYS = ['r', 'l', 'mid', 'cl', 'cr'];
    function hit(px, py) {
        var cur = S.cur, V = S.view;
        if (!cur || !V) return null;
        var best = null, bd = COARSE ? 34 : 24;
        KEYS.forEach(function (k) {
            var p = cur.marks[k];
            if (!p) return;
            var d = Math.hypot(V.x(p.x) - px, V.y(p.y) - py);
            if (d < bd) { bd = d; best = k; }
        });
        return best;
    }
    function evPos(e) { var r = cv.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; }

    cv.addEventListener('pointerdown', function (e) {
        if (S.state !== 'result' || !S.cur || !S.view) return;
        var p = evPos(e), k = hit(p.x, p.y);
        if (!k) return;
        e.preventDefault();
        var m = S.cur.marks[k], V = S.view;
        S.drag = { key: k, ox: V.x(m.x) - p.x, oy: V.y(m.y) - p.y, moved: false };
        S.sel = k;
        try { cv.setPointerCapture(e.pointerId); } catch (_) { }
        cv.classList.add('dragging');
        drawReview();
        drawLoupe(p, k);
    });
    cv.addEventListener('pointermove', function (e) {
        if (!S.drag) {
            if (S.state === 'result' && S.view && e.pointerType === 'mouse') cv.style.cursor = hit(evPos(e).x, evPos(e).y) ? 'grab' : '';
            return;
        }
        e.preventDefault();
        var p = evPos(e), V = S.view, k = S.drag.key;
        var nx = V.ix(p.x + S.drag.ox), ny = V.iy(p.y + S.drag.oy);
        var fr = S.cur.frame;
        S.cur.marks[k] = { x: clamp(nx, 0, fr.W - 1), y: clamp(ny, 0, fr.H - 1) };
        S.drag.moved = true;
        markEdited();
        drawLoupe(p, k);
    });
    function endDrag(e) {
        if (!S.drag) return;
        var k = S.drag.key, moved = S.drag.moved;
        S.drag = null;
        try { cv.releasePointerCapture(e.pointerId); } catch (_) { }
        cv.classList.remove('dragging');
        hideLoupe();
        if (moved) { ev('pd_edit', { k: k, src: S.cur.src }); renderResult(); pushRunUpdate(); }
        else drawReview();
    }
    cv.addEventListener('pointerup', endDrag);
    cv.addEventListener('pointercancel', endDrag);

    cv.addEventListener('keydown', function (e) {
        if (S.state !== 'result' || !S.cur || !S.view) return;
        var d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            var avail = KEYS.filter(function (k) { return S.cur.marks[k]; });
            S.sel = avail[(avail.indexOf(S.sel) + 1) % avail.length];
            announce(t('mk_sel_' + S.sel));
            drawReview();
            return;
        }
        if (!d) return;
        e.preventDefault();
        var step = (e.shiftKey ? 4 : 0.5) / S.view.s;
        var m = S.cur.marks[S.sel];
        if (!m) return;
        var sx = S.view.mirror ? -d[0] : d[0];
        S.cur.marks[S.sel] = { x: m.x + sx * step, y: m.y + d[1] * step };
        markEdited();
        renderResult();
        clearTimeout(S.kbT);
        S.kbT = setTimeout(pushRunUpdate, 600);
    });

    function markEdited() {
        S.cur.edited = true;
        recalc();
        drawReview();
        var r = S.cur.res;
        if (r) { $('pdFar').textContent = fh(r.pd); $('pdR').textContent = fh(r.R); $('pdL').textContent = fh(r.L); }
    }
    function pushRunUpdate() {
        // Правка уточняет ТЕКУЩИЙ замер, а не добавляет новый.
        if (!S.runs.length || !S.cur || !S.cur.res) return;
        S.runs[S.runs.length - 1].res = Object.assign({}, S.cur.res);
        var h = histLoad(); var c = combined();
        if (h.length && h[0].sid === sessionId) { h[0].pd = +c.pd.toFixed(2); h[0].R = +c.R.toFixed(2); h[0].L = +c.L.toFixed(2); h[0].pm = c.pm; histSave(h); }
        renderResult();
    }

    var loupe = $('pdLoupe');
    function drawLoupe(p, k) {
        var cur = S.cur, V = S.view, m = cur.marks[k];
        loupe.hidden = false;
        var size = 120, zoom = 3;
        var lx = p.x - size - 18, ly = p.y - size - 18;
        var sr = $('pdStage').getBoundingClientRect(), cr = cv.getBoundingClientRect();
        if (lx < 4) lx = p.x + 18;
        if (ly < 4) ly = p.y + 18;
        lx = clamp(lx, 4, cr.width - size - 4); ly = clamp(ly, 4, cr.height - size - 4);
        loupe.style.left = (lx + cr.left - sr.left) + 'px';
        loupe.style.top = (ly + cr.top - sr.top) + 'px';
        var lc = loupe.getContext('2d');
        var W = loupe.width, H = loupe.height;
        var srcHalf = (size / 2) / (V.s * zoom);
        lc.save();
        lc.clearRect(0, 0, W, H);
        lc.beginPath(); lc.arc(W / 2, H / 2, W / 2, 0, Math.PI * 2); lc.clip();
        lc.fillStyle = '#000'; lc.fillRect(0, 0, W, H);
        lc.imageSmoothingQuality = 'high';
        if (V.mirror) { lc.translate(W, 0); lc.scale(-1, 1); }
        lc.drawImage(cur.frame.canvas, m.x - srcHalf, m.y - srcHalf, srcHalf * 2, srcHalf * 2, 0, 0, W, H);
        lc.restore();
        lc.strokeStyle = k === 'cl' || k === 'cr' ? '#f5a524' : (k === 'mid' ? '#a78bfa' : '#38bdf8');
        lc.lineWidth = 2;
        lc.beginPath(); lc.moveTo(W / 2, H * 0.2); lc.lineTo(W / 2, H * 0.44); lc.moveTo(W / 2, H * 0.56); lc.lineTo(W / 2, H * 0.8);
        lc.moveTo(W * 0.2, H / 2); lc.lineTo(W * 0.44, H / 2); lc.moveTo(W * 0.56, H / 2); lc.lineTo(W * 0.8, H / 2); lc.stroke();
    }
    function hideLoupe() { loupe.hidden = true; }

    function syncZoomBtn() {
        var b = $('pdZoom'), l = S.zoom ? t('btn_fit') : t('btn_zoom');
        b.title = l;
        b.setAttribute('aria-label', l);
        b.setAttribute('aria-pressed', S.zoom ? 'true' : 'false');
    }

    /* ═══ проводка ═════════════════════════════════════════════════════════ */

    function wire() {
        // вкладки режимов: клик + стрелки (tablist)
        var tabs = Array.prototype.slice.call(document.querySelectorAll('.pd-tab'));
        tabs.forEach(function (b, i) {
            b.addEventListener('click', function () { setMode(b.getAttribute('data-mode')); });
            b.addEventListener('keydown', function (e) {
                var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
                if (!d) return;
                if (document.documentElement.dir === 'rtl') d = -d;
                e.preventDefault();
                var n = tabs[(i + d + tabs.length) % tabs.length];
                n.focus(); n.click();
            });
        });
        var opts = Array.prototype.slice.call(document.querySelectorAll('.pd-sw'));
        opts.forEach(function (b, i) {
            b.addEventListener('click', function () { setScale(b.getAttribute('data-scale')); ev('pd_scale', { s: b.getAttribute('data-scale') }); });
            b.addEventListener('keydown', function (e) {
                var d = (e.key === 'ArrowDown' || e.key === 'ArrowRight') ? 1 : (e.key === 'ArrowUp' || e.key === 'ArrowLeft') ? -1 : 0;
                if (!d) return;
                e.preventDefault();
                var n = opts[(i + d + opts.length) % opts.length];
                n.focus(); n.click();
            });
        });
        $('pdStart').addEventListener('click', function () {
            if (S.mode === 'photo') openPicker(); else startCamera();
        });
        // Модель — заранее, по первому признаку намерения, а не у всех подряд.
        ['pointerenter', 'focus', 'touchstart'].forEach(function (evn) {
            $('pdStart').addEventListener(evn, function () {
                try { if (window.FRLandmarks && S.mode === 'camera') window.FRLandmarks.warm(CFG.sources || [], { mode: 'VIDEO', numFaces: 1, matrix: true, downloaderUrl: CFG.mdUrl }); } catch (_) { }
            }, { passive: true, once: true });
        });
        $('pdStop').addEventListener('click', function () { stopLive('user'); });
        $('pdStop2').addEventListener('click', function () { stopLive('user'); });
        $('pdAgain').addEventListener('click', function () {
            ev('pd_again', { n: S.runs.length, src: S.cur ? S.cur.src : '', card: S.againCard ? 1 : 0 });
            if (S.againCard) {
                setScale('card');
                if (S.mode !== 'camera') setMode('camera');
                startCamera();
                return;
            }
            if (S.mode === 'photo') openPicker(); else startCamera();
        });
        $('pdRetake').addEventListener('click', function () {
            if (S.cur && S.cur.src === 'photo') openPicker();
            else { if (S.runs.length) S.runs.pop(); startCamera(); }
        });
        $('pdResetPts').addEventListener('click', function () {
            if (!S.cur) return;
            S.cur.marks = JSON.parse(JSON.stringify(S.cur.orig));
            S.cur.edited = false;
            recalc(); renderResult(); pushRunUpdate();
            ev('pd_reset_points', {});
        });
        $('pdZoom').addEventListener('click', function () { S.zoom = !S.zoom; syncZoomBtn(); drawReview(); });
        $('pdCopy').addEventListener('click', function () { copyText(summary(), 'all'); });
        var hero = $('pdFar');
        var copyHero = function () { var r = combined(); if (r) copyText(S.mode === 'known' ? f1(r.pd) : fh(r.pd), 'pd'); };
        hero.addEventListener('click', copyHero);
        hero.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copyHero(); } });
        $('pdShare').addEventListener('click', share);
        $('pdPdfBtn').addEventListener('click', makePdf);
        $('pdCalibReset').addEventListener('click', function () {
            S.calib = null;
            try { localStorage.removeItem(CKEY); } catch (_) { }
            syncCalib();
            ev('pd_calib_reset', {});
        });
        $('pdVoice').addEventListener('click', function () {
            S.voice = !S.voice;
            this.setAttribute('aria-pressed', S.voice ? 'true' : 'false');
            try { window.__toolSettings && window.__toolSettings.save(SLUG, 'voice', S.voice); } catch (_) { }
            if (S.voice) speak(t('voice_on'));
            ev('pd_voice', { on: S.voice ? 1 : 0 });
        });
        var kn = $('pdKnownIn'), kt = 0;
        kn.addEventListener('input', function () { clearTimeout(kt); kt = setTimeout(onKnownInput, 220); });
        $('pdRxPower').addEventListener('input', renderRx);
        $('pdRxProg').addEventListener('change', renderRx);
        $('pdHistClear').addEventListener('click', function () { histSave([]); try { localStorage.removeItem(HKEY); } catch (_) { } renderHistory(); });

        var up = $('pdUpload');
        if (up) {
            // Общая привязка без гонки: файл, выбранный до того, как этот
            // скрипт дочитался, не теряется.
            window.__bindUploaderFile(up, function (f) { if (f) onPhoto(f); });
            up.addEventListener('tool-uploader:clear', function () { S.cur = null; setState('idle'); });
        }
        // Фото можно бросить прямо на сцену — оно уходит в общую зону загрузки.
        var st = $('pdStage');
        st.addEventListener('dragover', function (e) { if (S.mode === 'photo' && S.state !== 'live') e.preventDefault(); });
        st.addEventListener('drop', function (e) {
            if (S.mode !== 'photo') return;
            var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
            if (!f) return;
            e.preventDefault();
            if (up && up.__uploaderInjectFile) up.__uploaderInjectFile(f, 'stage_drop');
        });

        if (window.ResizeObserver) new ResizeObserver(function () { requestAnimationFrame(redraw); }).observe($('pdStage'));
        window.addEventListener('pagehide', function () { if (S.live) stopLive('pagehide'); });
        document.addEventListener('visibilitychange', function () {
            if (document.hidden && S.live) stopLive('hidden');
        });
    }

    function openPicker() {
        var up = $('pdUpload');
        if (up && up.__uploaderOpen) up.__uploaderOpen();
    }

    function init(missing) {
        if (missing && missing.indexOf('PDCore') >= 0) { showErr(t('err_model')); return; }
        try {
            var sc = window.__toolSettings ? window.__toolSettings.load(SLUG, 'scale', 'card') : 'card';
            setScale(sc === 'iris' ? 'iris' : 'card');
            var rd = window.__toolSettings ? +window.__toolSettings.load(SLUG, 'reading', 400) : 400;
            if ([330, 400, 500, 650].indexOf(rd) >= 0) S.reading = rd;
            S.voice = !!(window.__toolSettings && window.__toolSettings.load(SLUG, 'voice', false));
            $('pdVoice').setAttribute('aria-pressed', S.voice ? 'true' : 'false');
        } catch (_) { }
        loadCalib();
        wire();
        setState('idle');
        renderGlasses(null);
        renderHistory();
        // ?mode=known|photo — ссылка из статьи или из соседнего тула
        try {
            var qm = new URLSearchParams(location.search).get('mode');
            if (qm === 'known' || qm === 'photo') setMode(qm);
        } catch (_) { }
        stageMark('ready', {});
    }

    // Тестовый крючок: состояние и результат без DOM-раскопок.
    window.__pupilDebug = {
        state: function () { return { mode: S.mode, state: S.state, scale: S.scale, runs: S.runs.length, cur: S.cur ? { src: S.cur.src, scale: S.cur.scale, res: S.cur.res, card: S.cur.card, edited: S.cur.edited, multi: S.cur.multi, frames: S.cur.frames } : null, known: S.known, calib: S.calib }; },
        marks: function () { return S.cur && S.cur.marks; },
        view: function () { return S.view; },
        setMark: function (k, x, y) { if (!S.cur) return; S.cur.marks[k] = { x: x, y: y }; markEdited(); renderResult(); },
    };

    whenReady(['PDCore', 'PDCard'], init);
})();
